import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { MapPinned, Plus, QrCode, ClipboardCheck, FileText } from 'lucide-react'
import {
    patrolService, Checkpoint, CheckpointType, PatrolTour, TourCompliance, DailyActivityReport,
} from '@/services/patrolService'

export default function PatrolPage() {
    const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([])
    const [cpForm, setCpForm] = useState({ name: '', code: '', type: 'QR' as CheckpointType, lat: '', lon: '', radiusMeters: '50' })
    const [routeName, setRouteName] = useState('')
    const [routeSel, setRouteSel] = useState<number[]>([])
    const [createdRouteId, setCreatedRouteId] = useState<number | null>(null)
    const [tour, setTour] = useState<PatrolTour | null>(null)
    const [compliance, setCompliance] = useState<TourCompliance | null>(null)
    const [scan, setScan] = useState({ checkpointId: '', scannedCode: '' })
    const [dar, setDar] = useState('')
    const [dars, setDars] = useState<DailyActivityReport[]>([])

    const load = () => {
        patrolService.listCheckpoints().then(setCheckpoints).catch(() => {})
        patrolService.myDars().then(setDars).catch(() => {})
    }
    useEffect(load, [])

    const createCheckpoint = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            await patrolService.createCheckpoint({
                name: cpForm.name, code: cpForm.code || undefined, type: cpForm.type,
                lat: cpForm.lat ? parseFloat(cpForm.lat) : undefined,
                lon: cpForm.lon ? parseFloat(cpForm.lon) : undefined,
                radiusMeters: parseInt(cpForm.radiusMeters) || 50,
            })
            toast.success('Checkpoint creado'); setCpForm({ ...cpForm, name: '', code: '' }); load()
        } catch { toast.error('Error al crear checkpoint') }
    }

    const createRoute = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!routeName || routeSel.length === 0) return toast.error('Nombre y al menos un checkpoint')
        try {
            const r = await patrolService.createRoute({ name: routeName, checkpointIds: routeSel })
            setCreatedRouteId(r.id); toast.success('Ruta creada'); setRouteName(''); setRouteSel([])
        } catch { toast.error('Error al crear ruta') }
    }

    const startTour = async () => {
        if (!createdRouteId) return toast.error('Crea una ruta primero')
        try { const t = await patrolService.startTour(createdRouteId); setTour(t); setCompliance(null); toast.success('Ronda iniciada') }
        catch { toast.error('No se pudo iniciar la ronda') }
    }

    const doScan = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!tour) return
        try {
            await patrolService.scan(tour.id, { checkpointId: parseInt(scan.checkpointId), scannedCode: scan.scannedCode })
            const c = await patrolService.compliance(tour.id); setCompliance(c)
            toast.success(c.complete ? 'Ronda completada' : 'Checkpoint escaneado')
            setScan({ checkpointId: '', scannedCode: '' })
        } catch { toast.error('Error al escanear') }
    }

    const submitDar = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!dar.trim()) return
        try { await patrolService.submitDar({ content: dar }); toast.success('Parte enviado'); setDar(''); load() }
        catch { toast.error('Error al enviar el parte') }
    }

    const toggle = (id: number) => setRouteSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])
    const inp = 'w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm'
    const card = 'bg-gray-800/40 border border-gray-700 rounded-lg p-4'

    return (
        <div className="p-6 text-gray-100">
            <div className="flex items-center gap-3 mb-6"><MapPinned className="text-indigo-400" /><h1 className="text-2xl font-bold">Rondas</h1></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Checkpoints */}
                <form onSubmit={createCheckpoint} className={card + ' space-y-3'}>
                    <h2 className="font-semibold flex items-center gap-2"><Plus size={16} /> Nuevo checkpoint</h2>
                    <input className={inp} placeholder="Nombre" value={cpForm.name} onChange={(e) => setCpForm({ ...cpForm, name: e.target.value })} />
                    <select className={inp} value={cpForm.type} onChange={(e) => setCpForm({ ...cpForm, type: e.target.value as CheckpointType })}>
                        <option value="QR">QR</option><option value="NFC">NFC</option><option value="GEO">GEO (radio)</option>
                    </select>
                    {cpForm.type !== 'GEO'
                        ? <input className={inp} placeholder="Código QR/NFC" value={cpForm.code} onChange={(e) => setCpForm({ ...cpForm, code: e.target.value })} />
                        : <div className="grid grid-cols-3 gap-2">
                            <input className={inp} placeholder="Lat" value={cpForm.lat} onChange={(e) => setCpForm({ ...cpForm, lat: e.target.value })} />
                            <input className={inp} placeholder="Lon" value={cpForm.lon} onChange={(e) => setCpForm({ ...cpForm, lon: e.target.value })} />
                            <input className={inp} placeholder="Radio m" value={cpForm.radiusMeters} onChange={(e) => setCpForm({ ...cpForm, radiusMeters: e.target.value })} />
                        </div>}
                    <button className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Crear checkpoint</button>
                </form>

                {/* Crear ruta */}
                <form onSubmit={createRoute} className={card + ' space-y-3'}>
                    <h2 className="font-semibold flex items-center gap-2"><Plus size={16} /> Nueva ruta</h2>
                    <input className={inp} placeholder="Nombre de la ruta" value={routeName} onChange={(e) => setRouteName(e.target.value)} />
                    <div className="max-h-32 overflow-y-auto space-y-1">
                        {checkpoints.map((c) => (
                            <label key={c.id} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={routeSel.includes(c.id)} onChange={() => toggle(c.id)} />
                                {c.name} <span className="text-gray-500">({c.type})</span>
                            </label>
                        ))}
                        {checkpoints.length === 0 && <p className="text-gray-500 text-sm">Crea checkpoints primero.</p>}
                    </div>
                    <button className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Crear ruta</button>
                </form>

                {/* Ejecutar ronda */}
                <div className={card + ' space-y-3'}>
                    <h2 className="font-semibold flex items-center gap-2"><QrCode size={16} /> Mi ronda</h2>
                    <button onClick={startTour} className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-2 text-sm font-medium">
                        Iniciar ronda {createdRouteId ? `(ruta ${createdRouteId})` : ''}
                    </button>
                    {tour && (
                        <form onSubmit={doScan} className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <input className={inp} placeholder="ID checkpoint" value={scan.checkpointId} onChange={(e) => setScan({ ...scan, checkpointId: e.target.value })} />
                                <input className={inp} placeholder="Código escaneado" value={scan.scannedCode} onChange={(e) => setScan({ ...scan, scannedCode: e.target.value })} />
                            </div>
                            <button className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Escanear</button>
                        </form>
                    )}
                    {compliance && (
                        <div className="text-sm flex items-center gap-2">
                            <ClipboardCheck size={16} className={compliance.complete ? 'text-emerald-400' : 'text-amber-400'} />
                            {compliance.verifiedCheckpoints}/{compliance.totalCheckpoints} verificados
                            {compliance.complete && <span className="text-emerald-400">· COMPLETA</span>}
                        </div>
                    )}
                </div>

                {/* DAR */}
                <div className={card + ' space-y-3'}>
                    <h2 className="font-semibold flex items-center gap-2"><FileText size={16} /> Parte diario (DAR)</h2>
                    <form onSubmit={submitDar} className="space-y-2">
                        <textarea className={inp} rows={3} placeholder="Novedades del turno…" value={dar} onChange={(e) => setDar(e.target.value)} />
                        <button className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Enviar parte</button>
                    </form>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                        {dars.map((d) => (
                            <div key={d.id} className="text-sm bg-gray-900/50 rounded px-3 py-2">
                                {d.content}<div className="text-gray-500 text-xs">{d.shiftDate}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
